import { cloneDeep } from 'es-toolkit';

import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import { DEFAULT_ARCHIVE_STATUSES, DEFAULT_LIVE_STATUSES } from '@/redux/modules/search/search.types';
import paramTypes from '@/types/search/enums/paramTypes';
import { SearchUrlStatus } from '@/types/search/enums/searchUrlStatus';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import {
    SEARCH_SEMANTIC_MULTI_VALUE_DELIMITER,
    buildBrowseCategoryPath,
    getBrowseDimensionKeyFromFacetType,
    getFacetTypeFromBrowseDimensionKey,
    parseBrowseCategoryPath,
    parseLegacyCategoryPath,
} from '@/utils/browsePath/browsePath';
import { formatHyphenatedDateOnly } from '@/utils/dates';
import { isEmpty } from '@/utils/isEmpty/isEmpty';

import FacetTypes from '../types/search/enums/facetTypes';
import { findCategoryContainingSomeCategoryId } from './search/searchUtils';
import { getCategoryLandingPageBaseUrl, getCategoryUrlWithParams, getPriceGuideBaseUrl } from './urls';

export { parse as parseQueryString, stringify as stringifyQuery } from 'query-string';
export const DEFAULT_STATUS_FOR_ONLINE_SEARCH = SearchUrlStatus.Online;
export const DEFAULT_STATUS_FOR_ARCHIVED_SEARCH = SearchUrlStatus.Archive;

/**
 * Params that should be preserved during server-side redirects.
 * These are typically params from email alerts that provide context about
 * what the user expects to see (new items, tracking, etc.)
 */
const REDIRECT_PRESERVED_PARAMS = [
    'publishDateMin', // Shows items published since last alert
    'utm_source', // Marketing/tracking params
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'from', // Identifies source (e.g., 'alert')
] as const;

type QueryParamMappingOptions = {
    useSemanticAttributeParams?: boolean;
};

const SPLIT_ANY_MULTIVALUE_REGEX = /[~,]/;

export const getQueryParamValueDelimiter = (paramName: string): string =>
    getFacetTypeFromBrowseDimensionKey(paramName) ? SEARCH_SEMANTIC_MULTI_VALUE_DELIMITER : ',';

const splitQueryParamMultiValue = (rawValue: unknown, delimiter: string): string[] => {
    const value = String(rawValue ?? '');
    if (!value) {
        return [];
    }
    if (delimiter === SEARCH_SEMANTIC_MULTI_VALUE_DELIMITER) {
        return value
            .split(SPLIT_ANY_MULTIVALUE_REGEX)
            .map((entry) => entry.trim())
            .filter(Boolean);
    }
    return value
        .split(delimiter)
        .map((entry) => entry.trim())
        .filter(Boolean);
};

/**
 * Extracts whitelisted params from query params object for use in redirects.
 * Returns a query string (with leading '?') if there are params to preserve, empty string otherwise.
 */
export const getPreservedRedirectParams = (queryParams: Record<string, any>): string => {
    if (!queryParams) {
        return '';
    }

    const preserved: Record<string, string> = {};

    for (const key of REDIRECT_PRESERVED_PARAMS) {
        if (queryParams[key] !== undefined && queryParams[key] !== null && queryParams[key] !== '') {
            preserved[key] = String(queryParams[key]);
        }
    }

    const entries = Object.entries(preserved);
    if (entries.length === 0) {
        return '';
    }

    const queryString = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
    return `?${queryString}`;
};

const addCurrencyRangeToParams = (
    params: any,
    paramName: string,
    min: string | number,
    max: string | number,
    currencyPrefix = 'USD:'
) => {
    let newParams = cloneDeep(params);
    const minValue = min !== '' && min !== undefined ? min : -1;
    const maxValue = max !== '' && max !== undefined ? max : -1;

    newParams[paramName] = `${currencyPrefix}${minValue}:${maxValue}`;
    return newParams;
};

const addSaleStartToParams = (params: any, min: number, max: number) => {
    let newParams = cloneDeep(params);
    if (!isNaN(min)) {
        newParams[paramTypes.SALE_DATE_MIN] = min;
    }
    if (!isNaN(max)) {
        newParams[paramTypes.SALE_DATE_MAX] = max;
    }
    return newParams;
};

const concatenatePath = (
    category: { categories?: { id: string }[]; id: string } | undefined,
    categoryId: string | number
): string[] => {
    if (!category) {
        return [];
    }
    if (String(category.id) === String(categoryId)) {
        return [category.id];
    }
    const nextCategory = findCategoryContainingSomeCategoryId(categoryId, category.categories);
    return [category.id, ...concatenatePath(nextCategory, categoryId)];
};

const modifyParamsRemoveCountry = (params) => {
    let newParams = cloneDeep(params);
    newParams = removeParamsFromList(newParams, paramTypes.COUNTRY_CODE_ID, params.country);
    return newParams;
};

const modifyParamsRemoveDistance = (params) => {
    let newParams = cloneDeep(params);
    newParams = removeParamsFromList(newParams, paramTypes.DISTANCE_ID, params.distance);
    return newParams;
};

export const modifyParamsRemoveState = (params: any) => {
    const newParams = cloneDeep(params);
    delete newParams[paramTypes.STATE_ID];
    return newParams;
};

const removeFacetFromUrl = (pathname, categoryId, params, baseUrl) => {
    // expecting a url in the pattern of "/c/name/name/id/id", "/c/name/id" or "/c"
    const url = pathname;
    // split the current location into its separate parts
    const splitUrl = url.split('/');
    // remove the empty string and the c (created because of the format '/c/')from the array
    const categoryNames = splitUrl.splice(2, splitUrl.length - 2);
    // split the category names and Ids (there will always be an even number of Ids and names in the pattern name/id or name/name/id/id etc.)
    const categoryIds = categoryNames.splice(categoryNames.length / 2, categoryNames.length - categoryNames.length / 2);

    let id = '';
    let name = '';
    // begin reassembling the two parts by adding each of the ones we want but not the one we don't
    for (let i = 0; i < categoryIds.length; i += 1) {
        if (categoryIds[i] !== categoryId) {
            id += `${categoryIds[i]}/`;
            name += `/${categoryNames[i]}`;
        }
    }

    if (name === '') {
        let url = baseUrl;
        if (!isEmpty(params)) {
            url = getCategoryUrlWithParams(url, params);
        }
        return url;
    }

    return getCategoryUrlWithParams(`/${splitUrl[1]}${name}/${id.substring(0, id.length - 1)}/`, params);
};

// Only exported for the tests
export const addValueToParamsList = (params: any, paramName: string, value: any, options?: { delimiter?: string }) => {
    let newParams = cloneDeep(params);
    const delimiter = options?.delimiter ?? getQueryParamValueDelimiter(paramName);
    const normalizedValue = String(value);
    if (newParams[paramName]) {
        const ids = splitQueryParamMultiValue(newParams[paramName], delimiter);
        if (!ids.some((id) => id === normalizedValue)) {
            ids.push(normalizedValue);
            newParams[paramName] = ids.join(delimiter);
        }
    } else {
        newParams[paramName] = normalizedValue;
    }
    return newParams;
};

export const getCategoryIdsFromQueryParams = (queryParams: any): string | undefined => {
    // TODO:
    // This IF should not be needed anymore, queryParams will never have a paramters property
    if (queryParams && queryParams.parameters) {
        try {
            const parameters = JSON.parse(decodeURIComponent(queryParams.parameters));
            if (parameters && parameters.category_id) {
                return parameters.category_id;
            }
        } catch {
            return undefined;
        }
    } else if (queryParams && queryParams.category_id) {
        return queryParams.category_id;
    }

    return undefined;
};

export const getFacetFromSearchQueryParameter = (paramKey: string) => {
    let facetId;
    if (paramKey === paramTypes.AUCTION_HOUSE_ID) {
        facetId = FacetTypes.AUCTION_HOUSE_ID;
    } else if (paramKey === paramTypes.COUNTRY_CODE_ID) {
        facetId = FacetTypes.COUNTRY_CODE_ID;
    } else if (paramKey === paramTypes.CURRENCY_ID) {
        facetId = FacetTypes.CURRENCY_ID;
    } else if (paramKey === paramTypes.CURRENT_BID_ID) {
        facetId = FacetTypes.CURRENT_BID_ID;
    } else if (paramKey === paramTypes.DISTANCE_ID) {
        facetId = FacetTypes.DISTANCE_ID;
    } else if (paramKey === paramTypes.SHIPPING) {
        facetId = FacetTypes.SHIPPING;
    } else if (paramKey === paramTypes.ESTIMATED_PRICE_ID) {
        facetId = FacetTypes.ESTIMATED_PRICE_ID;
    } else if (paramKey === paramTypes.HAMMER_PRICE_ID) {
        facetId = FacetTypes.HAMMER_PRICE_ID;
    } else if (paramKey === paramTypes.RESERVE) {
        facetId = FacetTypes.RESERVE;
    } else if (paramKey === paramTypes.SALE_TYPE_ID) {
        facetId = FacetTypes.SALE_TYPE_ID;
    } else if (paramKey === paramTypes.TOP_RATED_ID) {
        facetId = FacetTypes.TOP_RATED_ID;
    } else if (paramKey === paramTypes.PAYMENT) {
        facetId = FacetTypes.PAYMENT;
    } else if (paramKey === paramTypes.STATE_ID) {
        facetId = FacetTypes.STATE_ID;
    }
    const semanticFacetType = getFacetTypeFromBrowseDimensionKey(paramKey);
    if (!facetId && semanticFacetType) {
        facetId = semanticFacetType;
    }
    // Dynamic facets (Attrs.* from elastic) use identity mapping: param name = facet ID
    return facetId ?? paramKey;
};

export const getKeywordFromQueryParams = (queryParams: { [attribute: string]: string | number | boolean }) => {
    if (queryParams) {
        return queryParams['keyword'] || '';
    }
    return '';
};

export const getFilterTermFromQueryParams = (queryParams: { [attribute: string]: string | number | boolean }) => {
    if (queryParams) {
        return queryParams['filterTerm'] || '';
    }
    return '';
};

export const getPageFromQueryParams = (queryParams: any) => parseInt(queryParams.page, 10) || 1;

export const getPathWithoutParams = (pathname: string, facets: any) => {
    const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;

    const browseParsed = parseBrowseCategoryPath(normalizedPath);
    if (browseParsed) {
        return buildBrowseCategoryPath(browseParsed.categorySlug, [], { base: browseParsed.base });
    }

    const legacyParsed = parseLegacyCategoryPath(normalizedPath);
    if (legacyParsed) {
        return `${legacyParsed.base}/${legacyParsed.categorySlug}/${legacyParsed.numericCategoryId}/`;
    }

    const categoryFacets = facets.filter((fac) => fac.id === FacetTypes.CATEGORY_ID);
    let selectedCategoryFacet = [];
    if (categoryFacets.length) {
        selectedCategoryFacet = categoryFacets[0].categories.facets.filter((fac) => fac.selected);
    }
    // expecting a url in the pattern of "/c/name/name/id/id", "/c/name/id" or "/c"
    const url = pathname;
    // split the current location into its separate parts
    const splitUrl = url.split('/');
    // just gets the Id for the first category in the url
    const id = splitUrl[(splitUrl.length + 1) / 2];
    // just gets the name for the first category in the url
    const name = splitUrl[2];
    // if the length is 3 (meaning there's no categories in the url) or id is greater than or equal to the firstNonCategoryId (meaning it's a facet not a category)
    if (splitUrl.length <= 3 || selectedCategoryFacet.length <= 0) {
        return getCategoryLandingPageBaseUrl();
    }

    return `/${splitUrl[1]}/${name}/${id}`;
};

export const isArchivedSearch = (queryParams: any, pathname: string) => {
    let searchStatus = DEFAULT_LIVE_STATUSES;

    if (queryParams.status === 'archive' || (pathname && pathname.includes(getPriceGuideBaseUrl()))) {
        searchStatus = DEFAULT_ARCHIVE_STATUSES;
    }

    let archivedSearch = true;

    DEFAULT_ARCHIVE_STATUSES.forEach((status) => {
        archivedSearch = archivedSearch && searchStatus && searchStatus.some((x) => x === status);
    });

    return archivedSearch;
};

export const modifyParamsRemoveCampaignId = (params: any) => {
    let newParams = cloneDeep(params);
    newParams = removeParamsFromList(newParams, paramTypes.CAMPAIGN_ID, newParams.campaignId);
    return newParams;
};

export const modifyParamsRemoveCountryAndDistance = (params: any) => {
    let newParams = cloneDeep(params);
    newParams = modifyParamsRemoveCountry(newParams);
    newParams = modifyParamsRemoveDistance(newParams);
    newParams = modifyParamsRemoveState(newParams);
    return newParams;
};

export const modifyParamsReplaceCountry = (params: any, countryId: string) => {
    let newParams = cloneDeep(params);
    newParams = modifyParamsRemoveCountry(newParams);
    newParams = modifyParamsRemoveState(newParams);
    newParams = countryId !== 'US' ? modifyParamsRemoveDistance(newParams) : newParams;
    const paramName = parameterNameFromFacetId(FacetTypes.COUNTRY_CODE_ID);
    newParams = addValueToParamsList(newParams, paramName, countryId);
    return newParams;
};

export const modifyParamsReplaceState = (params: any, stateCode: string) => {
    let newParams = cloneDeep(params);
    newParams = modifyParamsRemoveState(newParams);
    newParams[paramTypes.STATE_ID] = stateCode;
    return newParams;
};

export const modifyParamsRemoveFacet = (params: any, facetId: string, options?: QueryParamMappingOptions) => {
    let newParams = cloneDeep(params);
    const paramName = parameterNameFromFacetId(facetId, options);
    delete newParams[paramName];
    return newParams;
};

export const modifyParamsRemovePublishDate = (params: any) => {
    let newParams = cloneDeep(params);
    delete newParams[paramTypes.PUBLISH_DATE_MIN];
    delete newParams[paramTypes.PUBLISH_DATE_MAX];
    return newParams;
};

export const modifyParamsRemovePage = (params: SearchUrlParams) => {
    const newParams = cloneDeep(params);
    delete newParams.page;
    return newParams;
};

export const modifyParamsRemoveSaveSearch = (params: any) => {
    const newParams = cloneDeep(params);
    delete newParams.saveSearch;
    return newParams;
};

export const modifyParamsRemoveParamByFacetId = (params: any, facetId, options?: QueryParamMappingOptions) => {
    const newParams = cloneDeep(params);
    delete newParams[parameterNameFromFacetId(facetId, options)];
    return newParams;
};

export const modifyParamsRemoveUnfollowSearch = (params: any) => {
    const newParams = cloneDeep(params);
    delete newParams.action;
    delete newParams.savedSearchId;
    delete newParams.signature;
    return newParams;
};

export const modifyParamsWhenChecked = (
    params: SearchUrlParams,
    facetId: string,
    value: any,
    checked: boolean,
    options?: QueryParamMappingOptions
) => {
    let newParams = cloneDeep(params);
    const paramName = parameterNameFromFacetId(facetId, options);
    const delimiter = getQueryParamValueDelimiter(paramName);
    if (checked) {
        newParams = addValueToParamsList(newParams, paramName, value, { delimiter });
        if (facetId === FacetTypes.COUNTRY_CODE_ID) {
            newParams = modifyParamsRemoveDistance(newParams);
            newParams = modifyParamsRemoveState(newParams);
        }
    } else {
        newParams = removeParamsFromList(newParams, paramName, value, { delimiter });
    }
    return newParams;
};

export const modifyParamsWithDate = (params: SearchUrlParams, date: any) => {
    const newParams = cloneDeep(params);
    if (date) {
        newParams.date = formatHyphenatedDateOnly(date);
    } else {
        delete newParams.date;
    }
    return newParams;
};

export const modifyAuctionCalendarParamsSaleStart = (params: SearchUrlParams, soonest: boolean) => {
    const newParams = cloneDeep(params);
    if (soonest) {
        newParams.sort = SearchSortAndDirection.TimeMostRecent;
    } else {
        delete newParams.sort;
    }
    return newParams;
};

export const modifyParamsWithDistance = (
    params: SearchUrlParams,
    country: string,
    zipCode: string,
    range: string,
    measurement: string
) => {
    let newParams = cloneDeep(params);
    newParams = modifyParamsReplaceCountry(newParams, country);
    const paramName = paramTypes.DISTANCE_ID;
    newParams[paramName] = `${country}:${zipCode}:${range}:${measurement}`;
    return newParams;
};

export const modifyParamsWithFilterTerm = (params: SearchUrlParams, filterTerm: string): SearchUrlParams => {
    const newFilterTerm = filterTerm.trim();
    const paramName = paramTypes.FILTER_TERM;
    const filterTermEntry = String(params[paramName]);
    let filterTerms = filterTermEntry.split(',');
    if (filterTerms.includes(newFilterTerm)) {
        return removeParamsFromList(params, paramName, filterTerm);
    } else {
        return addValueToParamsList(params, paramName, filterTerm);
    }
};

export const modifyParamsWithPage = (params: SearchUrlParams, pageNum: number) => {
    let newParams = cloneDeep(params);
    const paramName = paramTypes.PAGE;
    if (pageNum === 1) {
        delete newParams[paramName];
    } else {
        newParams[paramName] = pageNum;
    }
    return newParams;
};

export const modifyParamsWithPageSize = (params: SearchUrlParams, pageSize: number | 'all') => {
    const newParams = cloneDeep(params);
    newParams.pageSize = pageSize;
    return newParams;
};

export const modifyParamsWithRange = (params: any, facetId: string, min: string | number, max: string | number) => {
    let newParams = cloneDeep(params);
    const paramName = parameterNameFromFacetId(facetId);

    newParams =
        facetId === FacetTypes.SALE_START
            ? addSaleStartToParams(newParams, Number(min), Number(max))
            : addCurrencyRangeToParams(newParams, paramName, min, max);

    return newParams;
};

export const modifyParamsWithSort = (params: any, sortOption: string) => {
    let newParams = cloneDeep(params);
    const paramName = paramTypes.SORT;
    newParams[paramName] = sortOption;
    return newParams;
};

export const modifyParamsWithTaxonomyAll = (
    params: any,
    facetId: string,
    categoryId: string,
    options?: QueryParamMappingOptions
) => {
    let newParams = cloneDeep(params);
    const paramName = parameterNameFromFacetId(facetId, options);
    newParams = removeParamsFromList(newParams, paramName, categoryId, {
        delimiter: getQueryParamValueDelimiter(paramName),
    });
    return newParams;
};

export const modifyParamsWithTaxonomyParent = (
    params: any,
    facetId: number | string,
    categoryId: number | string,
    parentId: number | string,
    options?: QueryParamMappingOptions
) => {
    let newParams = cloneDeep(params);
    const paramName = parameterNameFromFacetId(facetId, options);
    const delimiter = getQueryParamValueDelimiter(paramName);
    newParams = removeParamsFromList(newParams, paramName, categoryId, { delimiter });
    newParams = addValueToParamsList(newParams, paramName, parentId, { delimiter });
    return newParams;
};

export const modifyParamsWithTaxonomySelected = (
    params: any,
    facetId: string,
    value: any,
    facets: any[],
    options?: QueryParamMappingOptions
) => {
    let newParams = cloneDeep(params);
    const paramName = parameterNameFromFacetId(facetId, options);
    const delimiter = getQueryParamValueDelimiter(paramName);
    const treeFacets = facets.filter((category) => category.id === facetId);

    const result = findCategoryContainingSomeCategoryId(value, treeFacets);

    const numbersToRemove = concatenatePath(result, value);
    if (numbersToRemove && numbersToRemove.length > 0) {
        numbersToRemove.forEach((idToRemove) => {
            if (idToRemove !== facetId || idToRemove !== value) {
                newParams = removeParamsFromList(newParams, paramName, idToRemove, { delimiter });
            }
        });
    }
    newParams = addValueToParamsList(newParams, paramName, value, { delimiter });

    newParams = modifyParamsRemovePage(newParams);

    return newParams;
};

// Only exported for the tests
export const parameterNameFromFacetId = (facetId: number | string, options?: QueryParamMappingOptions) => {
    if (options?.useSemanticAttributeParams) {
        const semanticParamName = getBrowseDimensionKeyFromFacetType(String(facetId));
        if (semanticParamName) {
            return semanticParamName;
        }
    }
    let paramName = '';
    if (facetId === FacetTypes.AUCTION_HOUSE_ID) {
        paramName = paramTypes.AUCTION_HOUSE_ID;
    } else if (
        facetId === FacetTypes.MATERIALS_AND_TECHNIQUES_ID ||
        facetId === FacetTypes.CREATOR_ID ||
        facetId === FacetTypes.STYLE_PERIOD_ID ||
        facetId === FacetTypes.CATEGORY_ID ||
        facetId === FacetTypes.ORIGIN_ID
    ) {
        paramName = paramTypes.CATEGORY_ID;
    } else if (facetId === FacetTypes.COUNTRY_CODE_ID) {
        paramName = paramTypes.COUNTRY_CODE_ID;
    } else if (facetId === FacetTypes.CURRENCY_ID) {
        paramName = paramTypes.CURRENCY_ID;
    } else if (facetId === FacetTypes.CURRENT_BID_ID) {
        paramName = paramTypes.CURRENT_BID_ID;
    } else if (facetId === FacetTypes.ESTIMATED_PRICE_ID) {
        paramName = paramTypes.ESTIMATED_PRICE_ID;
    } else if (facetId === FacetTypes.HAMMER_PRICE_ID) {
        paramName = paramTypes.HAMMER_PRICE_ID;
    } else if (facetId === FacetTypes.SALE_TYPE_ID) {
        paramName = paramTypes.SALE_TYPE_ID;
    } else if (facetId === FacetTypes.SHIPPING) {
        paramName = paramTypes.SHIPPING;
    } else if (facetId === FacetTypes.TOP_RATED_ID) {
        paramName = paramTypes.TOP_RATED_ID;
    } else if (facetId === FacetTypes.PAYMENT) {
        paramName = paramTypes.PAYMENT;
    } else if (facetId === FacetTypes.AUDIO_VIDEO) {
        paramName = paramTypes.LIVE;
    } else if (facetId === FacetTypes.STATE_ID) {
        paramName = paramTypes.STATE_ID;
    }

    // Dynamic facets (Attrs.* from elastic) use identity mapping: facet ID = param name
    return paramName || String(facetId);
};

// Based off https://stackoverflow.com/a/3855394/148256
export const parseQueryParams = <T extends object>(queryString: string): T & SearchUrlParams => {
    if (!queryString) {
        return {} as T & SearchUrlParams;
    }

    let params: any = (/^[?#]/.test(queryString) ? queryString.slice(1) : queryString)
        .split('&')
        .reduce((params, param) => {
            try {
                const [k, v] = param.split('=');
                let key = decodeURIComponent(k.replace(/\+/g, ' ')) || '';
                // Lowercasing the keys will make the front end code cleaner (and hopefully will not break things)
                // key = key.toLowerCase();
                // If the value is undefined than return a empty string.
                const value = typeof v === 'undefined' ? '' : decodeURIComponent(v.replace(/\+/g, ' '));
                // If there is a key, and it is not a duplicate, set it
                if (key && !params[key]) {
                    // Clean up the nasty parameters object
                    params[key] = key === 'parameters' && typeof value === 'string' ? JSON.parse(value) : value;
                }
            } catch {
                // If we catch an error, don't alter params, the reduce funtion will continue on without that param
            }
            return params;
        }, {});

    // Combine the parameters object
    if (params.parameters) {
        params =
            typeof params.parameters === 'object'
                ? {
                      ...params.parameters,
                      ...params,
                  }
                : params;
        delete params.parameters;
    }

    // Make sure we use campaign_id instead of campaignId
    if (params.campaignId || params.campaign_id) {
        params.campaign_id = params.campaignId || params.campaign_id;
        if (params.campaignId) {
            delete params.campaignId;
        }
    }

    // Make sure category_id is a string
    if (params.category_id) {
        params.category_id = params.category_id.toString();
    }
    // Make sure page is an int
    if (params.page) {
        params.page = parseInt(params.page, 10);
    }

    // Make sure page is an int (or "all")
    if (params.pageSize) {
        params.pageSize = params.pageSize === 'all' ? 'all' : parseInt(params.pageSize, 10);
    }

    // sort should always be a string
    if (params.sort) {
        if (typeof params.sort !== 'string') {
            delete params.sort;
        }
    }

    // location should always be a string
    if (params.location) {
        if (typeof params.location !== 'string') {
            delete params.location;
        }
    }

    return params;
};

export const removeArchiveFacetFromUrl = (pathname: string, categoryId: string, params: any) =>
    removeFacetFromUrl(pathname, categoryId, params, getPriceGuideBaseUrl());

// Only exported for the tests
export const removeParamsFromList = (params: any, paramName: string, value: any, options?: { delimiter?: string }) => {
    let newParams = cloneDeep(params);
    const delimiter = options?.delimiter ?? getQueryParamValueDelimiter(paramName);
    const normalizedValue = String(value);

    if (Reflect.has(newParams, paramName)) {
        const values = splitQueryParamMultiValue(newParams[paramName], delimiter);
        const index = values.indexOf(normalizedValue);
        if (index > -1) {
            values.splice(index, 1);
        }

        if (values.length === 0) {
            delete newParams[paramName];
        } else {
            newParams[paramName] = values.join(delimiter);
        }
    }
    return newParams;
};

export const removeQueryParams = (queryParams: any, keys: string[]) => {
    const query = {
        ...queryParams,
    };
    keys.map((k) => delete query[k]);
    return query;
};

export const removeUpcomingFacetFromUrl = (pathname: string, categoryId: string, params: any) =>
    removeFacetFromUrl(pathname, categoryId, params, getCategoryLandingPageBaseUrl());

type ValidatedQueryParams = {
    buyNow?: boolean | string;
    filter: string;
    page: number;
    sort: string;
    tab: string;
    terms: string;
};

// TODO this should use SearchSortEnum
export const validateQueryParams = (
    query: Partial<ValidatedQueryParams>,
    sortOptions?: { [sortId: string]: string } | false,
    defaultTab: string = 'upcoming',
    filterOptions?: { [sortId: string]: string } | false
    // buyNow could be a string when it comes in, but it will be a boolean when it goes back out
): ValidatedQueryParams & { buyNow: boolean } => {
    const queryPage = query?.page || 0;
    const querySort = query?.sort || '';
    const queryFilter = query?.filter || '';
    const page = !isNaN(queryPage) && queryPage > 0 ? Number(queryPage) : 1;
    const sort = sortOptions?.[querySort] ? querySort : '';
    const filter = filterOptions?.[queryFilter] ? queryFilter : '';
    const tab = query?.tab || defaultTab;
    const terms = query?.terms || '';
    const buyNow = query?.buyNow === 'false' ? false : Boolean(query?.buyNow);

    return {
        buyNow,
        filter,
        page,
        sort,
        tab,
        terms,
    };
};

export const makeAllCategoriesQueryString = (ids: number[]) => {
    if (ids.length === 0) {
        return '?';
    }
    return `?${ids.map((id, index) => `${index}=${id}`).join('&')}`;
};
